import React, { useEffect, useMemo, useState } from 'react';
import AppShell from '@/components/layout/AppShell';
import { ownerNav } from '@/lib/navConfig';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Save, RotateCcw, Eye, Printer, HelpCircle } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const DOC_META = [
  { key: 'so',              label: 'Sales Order (SO)' },
  { key: 'delivery_order',  label: 'Surat Jalan' },
  { key: 'invoice',         label: 'Invoice' },
];

const PLACEHOLDER_GROUPS = [
  {
    title: 'Perusahaan',
    items: [
      ['{{ company_name }}',        'Nama perusahaan'],
      ['{{ company_address }}',     'Alamat perusahaan'],
      ['{{ company_phone }}',       'Telepon perusahaan'],
      ['{{ tax_number }}',          'NPWP perusahaan'],
      ['{{ bank_name }}',           'Nama bank'],
      ['{{ bank_account_no }}',     'Nomor rekening'],
      ['{{ bank_account_holder }}', 'Atas nama rekening'],
    ],
  },
  {
    title: 'Nomor & Tanggal',
    items: [
      ['{{ invoice_no }}',      'Nomor invoice'],
      ['{{ so_no }}',           'Nomor SO'],
      ['{{ do_no }}',           'Nomor surat jalan'],
      ['{{ tanggal }}',         'Tanggal (DD/MM/YYYY)'],
      ['{{ tanggal_lengkap }}', 'Tanggal + jam'],
      ['{{ due_date }}',        'Jatuh tempo'],
      ['{{ delivery_date }}',   'Tanggal kirim'],
    ],
  },
  {
    title: 'Customer',
    items: [
      ['{{ customer_name }}',     'Nama customer'],
      ['{{ customer_company }}',  'Perusahaan customer'],
      ['{{ customer_address }}',  'Alamat customer'],
      ['{{ customer_whatsapp }}', 'WhatsApp customer'],
      ['{{ customer_npwp }}',     'NPWP customer'],
    ],
  },
  {
    title: 'Pengiriman (Surat Jalan)',
    items: [
      ['{{ delivery_address }}', 'Alamat pengiriman'],
      ['{{ driver_name }}',      'Nama sopir'],
      ['{{ vehicle_plate }}',    'Plat kendaraan'],
    ],
  },
  {
    title: 'Sales & Kasir',
    items: [
      ['{{ salesman_name }}', 'Nama sales'],
      ['{{ kasir_name }}',    'Nama kasir'],
    ],
  },
  {
    title: 'Pembayaran & Total',
    items: [
      ['{{ payment_method }}',   'Metode: Tunai/Transfer/QRIS/Tempo'],
      ['{{ total }}',            'Total (Rp x.xxx.xxx)'],
      ['{{ paid_amount }}',      'Sudah dibayar (DP)'],
      ['{{ remaining_amount }}', 'Sisa tagihan'],
      ['{{ terbilang }}',        'Total dalam huruf'],
    ],
  },
  {
    title: 'Item (di dalam loop)',
    items: [
      ['{% for item in items %}',       'Mulai loop tiap item'],
      ['{{ item.no }}',                 'Nomor urut item'],
      ['{{ item.sku }}',                'SKU / kode barang'],
      ['{{ item.name }}',               'Nama barang'],
      ['{{ item.qty }}',                'Kuantitas (angka)'],
      ['{{ item.unit_price }}',         'Harga satuan (Rp)'],
      ['{{ item.subtotal }}',           'Subtotal (Rp)'],
      ['{% endfor %}',                  'Akhir loop'],
    ],
  },
  {
    title: 'Rata Kolom (Filter)',
    items: [
      ['{{ "%-25s"|format(item.name) }}',   'Rata kiri 25 karakter'],
      ['{{ "%13s"|format(item.subtotal) }}','Rata kanan 13 karakter'],
      ['{{ item.name | ljust(25) }}',       'Filter ljust (alternatif)'],
      ['{{ total | rjust(15) }}',           'Filter rjust'],
    ],
  },
];

/** Escape HTML entities pada string agar aman disisipkan sebagai text.
 *  Digunakan untuk mencegah XSS pada title/label yang di-inject ke jendela print. */
function _escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Build CSS string for the print window given orientation. Kept as a pure
 *  function to avoid duplicating the huge CSS string in setOrient. */
function _buildPrintCss(orientation) {
  const size = orientation === 'landscape' ? '11in 9.5in' : '9.5in 11in';
  return `@page { size: ${size}; margin: 0.35in 0.4in; }
html, body { margin: 0; padding: 0; }
body {
  font-family: 'Courier New', 'Consolas', 'Lucida Console', monospace;
  font-size: 10pt; line-height: 1.2; white-space: pre;
  color: #000; background: #fff; font-weight: bold;
}
pre {
  margin: 0; font-family: inherit; font-size: inherit; line-height: inherit;
  white-space: pre; font-weight: bold;
  text-shadow: 0.35px 0 0 currentColor, -0.35px 0 0 currentColor;
}
.toolbar {
  padding: 10px 14px; background: #f3f4f6; border-bottom: 1px solid #ddd;
  font-family: system-ui, -apple-system, Segoe UI, sans-serif; font-size: 12px;
  display: flex; gap: 12px; align-items: center; flex-wrap: wrap;
}
.toolbar b { color: #111; }
.toolbar .segbtn { display: inline-flex; gap: 0; border: 1px solid #cbd5e1; border-radius: 6px; overflow: hidden; }
.toolbar .segbtn button { padding: 4px 10px; border: 0; background: #fff; cursor: pointer; font-size: 12px; }
.toolbar .segbtn button.active { background: #0f766e; color: #fff; }
.toolbar .btn { padding: 5px 14px; font-size: 12px; cursor: pointer; border: 1px solid #cbd5e1; background: #fff; border-radius: 6px; }
.toolbar .btn.primary { background: #0f766e; color: #fff; border-color: #0f766e; }
@media print { .toolbar { display: none !important; } }`;
}

/** Buka jendela cetak baru berisi teks monospace + auto trigger print dialog.
 *  Hardened terhadap XSS: title di-escape, konten disisipkan via textContent,
 *  dan CSS di-inject sebagai style.textContent (bukan HTML string).
 *  @param {string} text - Rendered plain text (monospace)
 *  @param {string} title - Judul jendela (di-escape sebelum ditulis ke DOM)
 *  @param {'portrait'|'landscape'} orientation
 */
export function openPrintWindow(text, title = 'Print', orientation = 'portrait') {
  const w = window.open('', '_blank', 'width=900,height=720');
  if (!w) {
    toast.error('Popup diblokir. Izinkan popup untuk mencetak dot matrix.');
    return;
  }
  const initialOrient = orientation === 'landscape' ? 'landscape' : 'portrait';
  const safeTitle = _escapeHtml(title);

  // 1) Tulis kerangka dokumen (statik, tanpa konten user). Hanya <title> yang di-inject dan sudah di-escape.
  //    document.write dipakai untuk boot dokumen kosong; setelah itu semua konten diinject via DOM API.
  const doc = w.document;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"/><title>${safeTitle}</title><style id="page-style"></style></head><body></body></html>`);
  doc.close();

  // 2) Isi CSS via textContent (bukan HTML). Aman dari injection karena tidak parsing HTML.
  const styleEl = doc.getElementById('page-style');
  if (styleEl) styleEl.textContent = _buildPrintCss(initialOrient);

  // 3) Build toolbar via DOM API + event listeners (bukan inline onclick).
  const toolbar = doc.createElement('div');
  toolbar.className = 'toolbar';

  const info = doc.createElement('span');
  info.textContent = '📄 Letter Continuous 9.5" × 11" — Dot Matrix.';
  toolbar.appendChild(info);

  const orientLabel = doc.createElement('span');
  orientLabel.textContent = 'Orientasi:';
  toolbar.appendChild(orientLabel);

  const segWrap = doc.createElement('span');
  segWrap.className = 'segbtn';

  const btnPortrait = doc.createElement('button');
  btnPortrait.id = 'op-portrait';
  btnPortrait.textContent = 'Portrait';
  const btnLandscape = doc.createElement('button');
  btnLandscape.id = 'op-landscape';
  btnLandscape.textContent = 'Landscape';

  const setOrient = (o) => {
    styleEl.textContent = _buildPrintCss(o);
    btnPortrait.className = o === 'portrait' ? 'active' : '';
    btnLandscape.className = o === 'landscape' ? 'active' : '';
  };
  btnPortrait.addEventListener('click', () => setOrient('portrait'));
  btnLandscape.addEventListener('click', () => setOrient('landscape'));

  segWrap.appendChild(btnPortrait);
  segWrap.appendChild(btnLandscape);
  toolbar.appendChild(segWrap);

  const btnPrint = doc.createElement('button');
  btnPrint.className = 'btn primary';
  btnPrint.textContent = '🖨 Cetak';
  btnPrint.addEventListener('click', () => w.print());
  toolbar.appendChild(btnPrint);

  const btnClose = doc.createElement('button');
  btnClose.className = 'btn';
  btnClose.textContent = 'Tutup';
  btnClose.addEventListener('click', () => w.close());
  toolbar.appendChild(btnClose);

  // 4) Konten <pre> menggunakan textContent — TIDAK ada parsing HTML sama sekali (safe).
  const pre = doc.createElement('pre');
  pre.textContent = String(text || '');

  doc.body.appendChild(toolbar);
  doc.body.appendChild(pre);

  // 5) Set active state awal dan auto-print
  setOrient(initialOrient);
  w.addEventListener('load', () => setTimeout(() => w.print(), 300));
  // Bila dokumen sudah load sebelum listener terpasang, tetap trigger.
  if (doc.readyState === 'complete') {
    setTimeout(() => w.print(), 300);
  }
}

export default function OwnerPrintTemplates() {
  const [templates, setTemplates] = useState({});
  const [defaults, setDefaults] = useState({});
  const [orientations, setOrientations] = useState({});
  const [active, setActive] = useState('so');
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/print-templates');
      setTemplates(data.templates || {});
      setDefaults(data.defaults || {});
      setOrientations(data.orientations || {});
      setDrafts(data.templates || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal memuat template');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const currentDraft = drafts[active] ?? '';
  const currentOrientation = orientations[active] ?? 'portrait';
  const isDirty = useMemo(() => (drafts[active] ?? '') !== (templates[active] ?? ''), [drafts, templates, active]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/print-templates/${active}`, { template: currentDraft });
      toast.success('Template tersimpan');
      setTemplates((t) => ({ ...t, [active]: currentDraft }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const setOrientation = async (o) => {
    const prev = orientations[active];
    setOrientations((oo) => ({ ...oo, [active]: o }));
    try {
      await api.put(`/print-templates/${active}/orientation`, { orientation: o });
      toast.success(`Default orientasi ${active}: ${o}`);
    } catch (e) {
      setOrientations((oo) => ({ ...oo, [active]: prev }));
      toast.error(e?.response?.data?.detail || 'Gagal menyimpan orientasi');
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/print-templates/${active}/reset`);
      setTemplates((t) => ({ ...t, [active]: data.template }));
      setDrafts((d) => ({ ...d, [active]: data.template }));
      toast.success('Template dikembalikan ke default');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal reset');
    } finally { setSaving(false); }
  };

  const preview = async () => {
    try {
      if (isDirty) {
        await api.put(`/print-templates/${active}`, { template: currentDraft });
        setTemplates((t) => ({ ...t, [active]: currentDraft }));
        toast.info('Draft disimpan lalu ditampilkan preview');
      }
      const res = await api.get(`/print-templates/${active}/preview`, { responseType: 'text' });
      openPrintWindow(res.data, `Preview ${active}`, currentOrientation);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal preview');
    }
  };

  const insertPlaceholder = (text) => {
    const el = document.getElementById(`tpl-editor-${active}`);
    if (!el) return;
    const start = el.selectionStart ?? currentDraft.length;
    const end = el.selectionEnd ?? currentDraft.length;
    const next = currentDraft.slice(0, start) + text + currentDraft.slice(end);
    setDrafts((d) => ({ ...d, [active]: next }));
    // restore focus + caret
    setTimeout(() => {
      el.focus();
      const pos = start + text.length;
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  return (
    <AppShell nav={ownerNav} title="Template Cetak (Dot Matrix)">
      <div className="space-y-4">
        <Card>
          <CardContent className="p-4 text-sm text-muted-foreground">
            <div className="flex items-start gap-2">
              <HelpCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="space-y-1">
                <div><b>Kertas:</b> Letter Continuous <b>9.5" x 11"</b> — Portrait ≈ 85 kolom, Landscape ≈ 100 kolom (@10 CPI). Tulisan otomatis <b>bold</b> untuk hasil dot matrix yang tebal.</div>
                <div><b>Cara pakai:</b> edit template + pilih orientasi default di sini → klik <b>Preview</b>, atau langsung buka halaman <b>Cetak Dokumen</b> → tombol <b>Dot Matrix</b>. Orientasi masih bisa diganti di jendela print.</div>
                <div><b>Format:</b> plain text monospace (Courier). Gunakan spasi untuk merapikan kolom. Placeholder di sisi kanan bisa diklik untuk disisipkan.</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs value={active} onValueChange={setActive}>
          <TabsList data-testid="tpl-tabs">
            {DOC_META.map((d) => (
              <TabsTrigger key={d.key} value={d.key} data-testid={`tpl-tab-${d.key}`}>{d.label}</TabsTrigger>
            ))}
          </TabsList>
          {DOC_META.map((d) => (
            <TabsContent key={d.key} value={d.key} className="mt-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Editor */}
                <Card className="lg:col-span-2">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="text-sm font-medium">Template {d.label}</div>
                      <div className="flex gap-2 items-center flex-wrap">
                        {/* Orientation segment */}
                        <div className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-muted/30 px-2 py-1 text-[11px]">
                          <span className="text-muted-foreground">Orientasi:</span>
                          <button
                            type="button"
                            onClick={() => setOrientation('portrait')}
                            className={`px-2 py-0.5 rounded ${currentOrientation === 'portrait' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
                            data-testid={`tpl-orient-portrait-${d.key}`}
                          >Portrait</button>
                          <button
                            type="button"
                            onClick={() => setOrientation('landscape')}
                            className={`px-2 py-0.5 rounded ${currentOrientation === 'landscape' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
                            data-testid={`tpl-orient-landscape-${d.key}`}
                          >Landscape</button>
                        </div>
                        <Button
                          size="sm" variant="outline"
                          onClick={preview}
                          data-testid={`tpl-preview-${d.key}`}
                        >
                          <Eye className="h-4 w-4 mr-1.5" /> Preview / Tes Cetak
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" data-testid={`tpl-reset-${d.key}`}>
                              <RotateCcw className="h-4 w-4 mr-1.5" /> Reset
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Reset template {d.label}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Template akan dikembalikan ke format bawaan (default). Perubahan Anda akan hilang.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Batal</AlertDialogCancel>
                              <AlertDialogAction onClick={reset}>Ya, Reset</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <Button
                          size="sm"
                          onClick={save}
                          disabled={!isDirty || saving}
                          data-testid={`tpl-save-${d.key}`}
                        >
                          <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Menyimpan...' : 'Simpan'}
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap text-[11px] text-muted-foreground">
                      <span>Panjang: <b className="text-foreground tabular-nums">{currentDraft.length}</b> karakter</span>
                      <span>•</span>
                      <span>Baris: <b className="text-foreground tabular-nums">{currentDraft.split('\n').length}</b></span>
                      {isDirty && <span className="text-amber-600 font-medium">• Belum disimpan</span>}
                    </div>

                    <Textarea
                      id={`tpl-editor-${d.key}`}
                      value={currentDraft}
                      onChange={(e) => setDrafts((dd) => ({ ...dd, [d.key]: e.target.value }))}
                      className="font-mono text-xs h-[520px] whitespace-pre"
                      spellCheck={false}
                      placeholder={loading ? 'Memuat...' : 'Isi template di sini...'}
                      data-testid={`tpl-editor-textarea-${d.key}`}
                    />

                    <div className="text-[11px] text-muted-foreground">
                      Tip: bar 85 karakter pemisah — <span className="font-mono">-------------------------------------------------------------------------------------</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Placeholder reference */}
                <Card className="lg:col-span-1">
                  <CardContent className="p-4 space-y-3">
                    <div className="text-sm font-medium">Placeholder Tersedia</div>
                    <div className="text-[11px] text-muted-foreground">Klik untuk menyisipkan ke posisi kursor.</div>
                    <div className="space-y-3 max-h-[560px] overflow-y-auto pr-1">
                      {PLACEHOLDER_GROUPS.map((g) => (
                        <div key={g.title} className="space-y-1.5">
                          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{g.title}</div>
                          <div className="space-y-1">
                            {g.items.map(([code, desc]) => (
                              <button
                                key={code}
                                type="button"
                                onClick={() => insertPlaceholder(code)}
                                className="w-full text-left rounded border border-border/60 hover:border-primary hover:bg-accent/60 transition-colors px-2 py-1.5 group"
                                data-testid={`tpl-insert-${code.replace(/[^a-z0-9]/gi, '-')}`}
                              >
                                <div className="font-mono text-[11px] text-primary group-hover:text-primary/90">{code}</div>
                                <div className="text-[10px] text-muted-foreground">{desc}</div>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </AppShell>
  );
}
